<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';
import PipelinesAPI from 'dashboard/api/pipelines';
import DealsAPI from 'dashboard/api/deals';
import ContactAPI from 'dashboard/api/contacts';
import EditStageDialog from '../components/EditStageDialog.vue';
import ColumnSidePanel from '../components/ColumnSidePanel.vue';
import DeleteStageDialog from '../components/DeleteStageDialog.vue';
import ImportLeadsCsvDialog from '../components/ImportLeadsCsvDialog.vue';
import NewDealDialog from '../components/NewDealDialog.vue';
import NewStageDialog from '../components/NewStageDialog.vue';
import NewPipelineDialog from '../components/NewPipelineDialog.vue';
import EditPipelineDialog from '../components/EditPipelineDialog.vue';
import DeletePipelineDialog from '../components/DeletePipelineDialog.vue';
import DisparadoresModal from '../components/DisparadoresModal.vue';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { getKanbanCache, setKanbanCache } from './kanbanCache';
import { branchColor } from 'dashboard/helper/branchColors';
/* global axios */

// Fase 3.2 · permisos granulares · admins/legacy mode → can() devuelve true
const { can, isAdminOrSuperAdmin, isSuperAdmin } = usePermissions();
// Antes era un whitelist hardcoded por email; ahora respeta chat.view granular.
const canSeeChat = computed(() => can('chat.view'));

// T2 · usuario actual para filtro "Míos"
const store = useStore();
const currentUserId = computed(() => store.getters.getCurrentUserID);

// Eliminar lead del Kanban (Victor 2026-05-30) · SuperAdmin (Victors 1/3) o
// Victor Manuel (7) por whitelist. Espejo del backend AgentPermissions.can_delete_lead?
const LEAD_DELETER_USER_IDS = [7];
const canDeleteLead = computed(
  () => isSuperAdmin.value || LEAD_DELETER_USER_IDS.includes(currentUserId.value)
);

const route = useRoute();
const router = useRouter();

const pipeline = ref(null);
const dealsByStage = ref({}); // { [stageId]: [deal, deal, ...] }
const loading = ref(true); // true = ni siquiera hay columnas todavía
const dealsLoading = ref(false); // true = columnas listas, tarjetas cargando
const error = ref(null);

const editingStage = ref(null);
const newDealStageId = ref(null);
const showNewDealDialog = ref(false);
const showNewStageDialog = ref(false);
// CM1 · Panel lateral de columna
const openPanelStageId = ref(null);
// Stage en proceso de eliminación (DeleteStageDialog)
const deletingStage = ref(null);
// Stage donde se está importando CSV
const importingStage = ref(null);

const pipelineId = computed(() => Number(route.params.pipelineId));
const accountId = computed(() => route.params.accountId);

const totalDeals = computed(() =>
  Object.values(dealsByStage.value).reduce((acc, arr) => acc + arr.length, 0)
);

// ── T2 toolbar · filtros y búsqueda client-side ─────────────────
const URGENT_DAYS_THRESHOLD = 7;
const searchQuery = ref('');
const activeFilter = ref('abiertos'); // 'abiertos' | 'mios' | 'urgentes' | 'atraso'

// ── Chips de filtro por sucursal (Victor 2026-06-09 · Opción A) ─────────
// Cada departamento tiene su propia lista de quién ve los chips SDO·SDE·Santiago·
// La Romana, para dar seguimiento a clientes de cualquier sucursal aunque su acceso
// normal sea de una sola. Espejo del backend AgentPermissions.BRANCH_CHIP_RULES.
//   - Impresión y Álbum: Victor (1,3), Victor Manuel (7), Mayerlin (36), Gabriela (41), Yeudy (18).
//   - Retoque: Victor (1,3), Victor Manuel (7), Gabriela (41), Elinne (35), Yeudy (18).
//   - Selección: Victor (1,3), Victor Manuel (7), Mayerlin (36), Gabriela (41), Elinne (35).
//   - Clientes: Victor (1,3), Victor Manuel (7), Gabriela (41).
//   - Cualquier OTRO departamento (default): Victor (1,3), Victor Manuel (7), Gabriela (41).
const BRANCH_CHIP_DEFAULT_IDS = [1, 3, 7, 41];
const BRANCH_CHIP_RULES = [
  { rx: /impresi|lbum/, ids: [1, 3, 7, 36, 41, 18] },
  { rx: /retoque/, ids: [1, 3, 7, 41, 35, 18] },
  { rx: /selecci/, ids: [1, 3, 7, 36, 41, 35] },
  { rx: /cliente/, ids: [1, 3, 7, 41] },
];
// color = color oficial de sucursal (facturación · Victor 2026-06-11), el mismo
// que se usa en la vista por sucursal del chat.
const BRANCH_CHIPS = [
  { key: 'sdo', label: 'SDO', ids: [2], color: branchColor('sdo') },
  { key: 'sde', label: 'SDE', ids: [3], color: branchColor('sde') },
  { key: 'santiago', label: 'Santiago', ids: [5], color: branchColor('santiago') },
  { key: 'romana', label: 'La Romana', ids: [4, 9], color: branchColor('romana') },
];
// Habilitado solo si el usuario está autorizado para ESTE departamento. Una regla
// específica gana; si ninguna aplica, los 3 base (Victor/Victor Manuel/Gabriela)
// lo ven igual. Sin embudo cargado → no aplica.
const canSeeBranchChips = computed(() => {
  const name = (pipeline.value?.name || '').toLowerCase().trim();
  if (!name) return false;
  const rule = BRANCH_CHIP_RULES.find(r => r.rx.test(name));
  const ids = rule ? rule.ids : BRANCH_CHIP_DEFAULT_IDS;
  return ids.includes(Number(currentUserId.value));
});
const branchChips = computed(() => (canSeeBranchChips.value ? BRANCH_CHIPS : []));
// null = las 4 sucursales · 'sdo'|'sde'|… = filtrar a esa.
const activeBranchKey = ref(null);
function toggleBranch(key) {
  activeBranchKey.value = activeBranchKey.value === key ? null : key;
  reloadBoardForBranch();
}

// Override SOLO de tablero (Victor 2026-06-10): recarga las etapas + clientes de
// la sucursal del chip SIN tocar la sucursal logeada (no usa branches/setActive,
// el selector de arriba queda igual). "La Romana" real es la branch 9 (la 4 quedó
// vacía). Sin chip → sin override: vuelve a las etapas de tu sucursal (ej. Personal).
async function reloadBoardForBranch() {
  const cfg = activeBranchKey.value
    ? BRANCH_CHIPS.find(c => c.key === activeBranchKey.value)
    : null;
  const branchId = cfg ? cfg.ids[cfg.ids.length - 1] : null;
  try {
    const pipelineRes = await PipelinesAPI.show(pipelineId.value, branchId);
    pipeline.value = pipelineRes.data;
    rebuildGroups(pipelineRes.data.stages, []);
    await loadDeals(pipelineRes.data.stages, { skipCache: !!branchId });
  } catch (_) {
    /* si falla, conserva el tablero anterior */
  }
}
// Preselecciona la sucursal activa del switcher (ej. Mayerlin entra y ve SDO).
// Si la activa no es una de las 4 (master/personal), queda en "todas". Se
// reinicia al cambiar de embudo (watch pipelineId más abajo).
let branchFilterInitialized = false;
watch(
  [branchChips, () => pipeline.value?.id],
  ([chips]) => {
    if (branchFilterInitialized || !chips.length) return;
    const activeId = Number(store.getters['branches/getActiveBranchId']);
    const match = chips.find(c => c.ids.includes(activeId));
    activeBranchKey.value = match ? match.key : null;
    branchFilterInitialized = true;
  },
  { immediate: true }
);

// Filtro "en atraso" (Victor 2026-06-08): el modal 911 navega aquí con
// ?atrasados=12,34,56 (los deal_id en atraso). Cuando viene, el Kanban muestra
// EXCLUSIVAMENTE esos clientes — sin importar el chip activo. Se quita con la X.
const atrasadosIds = computed(() => {
  const raw = route.query.atrasados;
  if (!raw) return null;
  const set = new Set(
    String(raw)
      .split(',')
      .map(n => Number(n))
      .filter(Boolean)
  );
  return set.size ? set : null;
});
function clearAtrasados() {
  const q = { ...route.query };
  delete q.atrasados;
  router.replace({ query: q });
}

// Buscador inteligente: cross-embudo + todos los campos · respeta visibilidad
// por rol (backend filtra: agente Vendedor/Selección solo sus asignados;
// admin/Gerente/Admin-sucursal todos los de la sucursal).
const globalResults = ref([]);
const searchingGlobal = ref(false);
let searchTimer = null;
watch(searchQuery, val => {
  clearTimeout(searchTimer);
  const q = (val || '').trim();
  if (q.length < 2) {
    globalResults.value = [];
    searchingGlobal.value = false;
    return;
  }
  searchingGlobal.value = true;
  searchTimer = setTimeout(async () => {
    try {
      const { data } = await DealsAPI.list({ q });
      globalResults.value = data.payload || [];
    } catch (_) {
      globalResults.value = [];
    } finally {
      searchingGlobal.value = false;
    }
  }, 300);
});
function openSearchResult(d) {
  searchQuery.value = '';
  globalResults.value = [];
  // Búsqueda cross-sucursal (Victor 2026-06-09): si el resultado es de OTRA
  // sucursal, cambiamos a esa sucursal activa antes de abrir, para que el cliente
  // se abra "en su sucursal" (contexto correcto del lead/chat).
  const bid = Number(d?.branch_id);
  if (bid && bid !== Number(store.getters['branches/getActiveBranchId'])) {
    store.dispatch('branches/setActive', bid);
  }
  // Mismo flujo que clic en tarjeta: SIEMPRE el chat moderno (abre/crea
  // conversación), nunca el modal de detalle del deal.
  openDealDetail(d);
}
// Etiqueta y color de la sucursal de un resultado de búsqueda (P1 · chip).
// SDO 2 · SDE 3 · Santiago 5 · La Romana 4/9.
const BRANCH_LABELS = { 2: 'SDO', 3: 'SDE', 5: 'Santiago', 4: 'La Romana', 9: 'La Romana' };
const BRANCH_CLASSES = { 2: 'br-sdo', 3: 'br-sde', 5: 'br-stg', 4: 'br-lr', 9: 'br-lr' };
function branchLabel(id) {
  return BRANCH_LABELS[Number(id)] || null;
}
function branchClass(id) {
  return BRANCH_CLASSES[Number(id)] || '';
}
// Monto del lead para el panel de búsqueda (P1): RD$/US$ con miles. Vacío si 0.
function dealAmount(d) {
  const v = Number(d?.value || 0);
  if (!v) return '';
  const sym = d.currency === 'USD' ? 'US$' : 'RD$';
  return `${sym}${v.toLocaleString('es-DO')}`;
}
// Chip de etapa con el color real de la etapa (texto + borde + fondo tenue).
function ksrStageStyle(stage) {
  const c = (stage?.color || '').startsWith('#') ? stage.color : '#9ca3af';
  return { color: c, borderColor: `${c}66`, background: `${c}22` };
}
const showGearMenu = ref(false);
const currentView = ref('kanban'); // 'kanban' | 'lista' (lista placeholder)
const showDisparadoresModal = ref(false);

function openDisparadoresModal() {
  closeGearMenu();
  showDisparadoresModal.value = true;
}

function isDealVisible(deal) {
  if (!deal) return false;
  // 1) Búsqueda fuzzy: title, contacto.name, contacto.phone, contacto.email
  const q = searchQuery.value.trim().toLowerCase();
  if (q) {
    const hay = [
      deal.title,
      deal.contact?.name,
      deal.contact?.phone_number,
      deal.contact?.email,
    ]
      .filter(Boolean)
      .map(s => String(s).toLowerCase())
      .join(' ');
    // Búsqueda inteligente de teléfono: compara solo dígitos para que
    // cualquier formato (+1, espacios, guiones, paréntesis) encuentre el lead.
    const qDigits = q.replace(/\D/g, '');
    const phoneDigits = String(
      deal.contact?.phone_number || deal.title || ''
    ).replace(/\D/g, '');
    const phoneMatch =
      qDigits.length >= 3 && phoneDigits.includes(qDigits);
    if (!hay.includes(q) && !phoneMatch) return false;
  }
  // 2) Filtro "en atraso" (desde el modal 911): tiene PRIORIDAD sobre los chips —
  // muestra solo los clientes que venían en atraso.
  if (atrasadosIds.value) {
    return atrasadosIds.value.has(Number(deal.id));
  }
  // 2.b) Chip de sucursal (Victor 2026-06-09): solo en Impresión/Álbum y para los
  // 4 autorizados. Si hay una sucursal activa, solo pasan los deals de esa sucursal
  // (SDO/SDE/Santiago/La Romana). El guard evita filtrar en otros embudos.
  if (canSeeBranchChips.value && activeBranchKey.value) {
    const cfg = branchChips.value.find(c => c.key === activeBranchKey.value);
    if (cfg && !cfg.ids.includes(Number(deal.branch_id))) return false;
  }
  // 3) Chip de filtro
  switch (activeFilter.value) {
    case 'mios':
      return deal.assignee_id != null && currentUserId.value != null
        && Number(deal.assignee_id) === Number(currentUserId.value);
    case 'urgentes':
      return (deal.days_in_stage || 0) >= URGENT_DAYS_THRESHOLD;
    case 'atraso':
      // En atraso (Victor 2026-06-11): reemplaza "Sin asignar". Usa la MISMA
      // definición que el latido del depto (Retoque/Impresión +10d, Álbum +45d,
      // sin etapas terminales ni Fiesta). En embudos sin umbral no marca ninguno.
      return dealBeats(deal);
    case 'abiertos':
    default:
      return true;
  }
}

const visibleCountsByStage = computed(() => {
  const out = {};
  for (const [stageId, deals] of Object.entries(dealsByStage.value)) {
    out[stageId] = (deals || []).filter(isDealVisible).length;
  }
  return out;
});

const totalVisibleLeads = computed(() =>
  Object.values(visibleCountsByStage.value).reduce((a, b) => a + b, 0)
);

const totalVisibleAmount = computed(() => {
  let sum = 0;
  for (const deals of Object.values(dealsByStage.value)) {
    for (const d of deals || []) {
      if (isDealVisible(d)) sum += Number(d.value || 0);
    }
  }
  return sum;
});

function formatAmountShort(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `RD$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `RD$${(n / 1_000).toFixed(1)}K`;
  return `RD$${n.toFixed(0)}`;
}

function setFilter(key) {
  activeFilter.value = key;
  // Si había un filtro "en atraso" activo, al elegir otro chip se sale de él.
  if (atrasadosIds.value) clearAtrasados();
}

function toggleGearMenu() {
  showGearMenu.value = !showGearMenu.value;
}
function closeGearMenu() { showGearMenu.value = false; }

function pipelineIcon() {
  return pipelineInitial(pipeline.value || {});
}

// ── Pipeline switcher (dropdown estilo C4 cristal negro) ─────────
const showPipelineSwitcher = ref(false);
const allPipelines = ref([]);
const pipelineSearchQuery = ref('');

const filteredPipelines = computed(() => {
  const q = pipelineSearchQuery.value.trim().toLowerCase();
  if (!q) return allPipelines.value;
  return allPipelines.value.filter(p => (p.name || '').toLowerCase().includes(q));
});

async function togglePipelineSwitcher() {
  if (showPipelineSwitcher.value) {
    showPipelineSwitcher.value = false;
    return;
  }
  try {
    const { data } = await PipelinesAPI.get();
    allPipelines.value = data.payload || [];
  } catch (_) { /* silencioso */ }
  pipelineSearchQuery.value = '';
  showPipelineSwitcher.value = true;
}
function closePipelineSwitcher() { showPipelineSwitcher.value = false; }
function selectPipeline(p) {
  closePipelineSwitcher();
  if (p.id === pipelineId.value) return;
  // Conserva el filtro "en atraso" al cambiar de departamento (Victor 2026-06-08):
  // el aviso 911 trae atrasos de Retoque/Impresión/Álbum → al saltar de embudo el
  // filtro se mantiene y cada departamento muestra SOLO sus clientes en atraso.
  const query = atrasadosIds.value ? { atrasados: route.query.atrasados } : {};
  router.push({ name: 'pipelines_view', params: { accountId: accountId.value, pipelineId: p.id }, query });
}
function pipelineInitial(p) {
  return (p?.name || '?').trim().charAt(0).toUpperCase();
}

// ── Crear nuevo embudo desde el dropdown ─────────
const showNewPipelineDialog = ref(false);
const showEditPipelineDialog = ref(false);
const showDeletePipelineDialog = ref(false);
function openEditPipelineDialog() {
  closePipelineSwitcher();
  showEditPipelineDialog.value = true;
}
function openDeletePipelineDialog() {
  closePipelineSwitcher();
  showDeletePipelineDialog.value = true;
}
function onPipelineUpdated(p) {
  showEditPipelineDialog.value = false;
  showDeletePipelineDialog.value = false;
  // Actualizar pipeline local con nuevos datos
  if (p) {
    pipeline.value = { ...pipeline.value, ...p };
    const idx = allPipelines.value.findIndex(pp => pp.id === p.id);
    if (idx >= 0) allPipelines.value[idx] = { ...allPipelines.value[idx], ...p };
  }
}
function onPipelineDeleted() {
  showDeletePipelineDialog.value = false;
  // Volver al home de pipelines · el listado se refresca solo
  router.push({ name: 'pipelines_home', params: { accountId: accountId.value } });
}
function openNewPipelineDialog() {
  closePipelineSwitcher();
  showNewPipelineDialog.value = true;
}
function onPipelineCreated(p) {
  showNewPipelineDialog.value = false;
  // Refresca el listado del switcher para que aparezca el nuevo embudo
  allPipelines.value = [...allPipelines.value, p];
  // Redirige al embudo recién creado
  router.push({ name: 'pipelines_view', params: { accountId: accountId.value, pipelineId: p.id } });
}

function rebuildGroups(stages, deals) {
  const grouped = {};
  for (const stage of stages || []) grouped[stage.id] = [];
  for (const deal of deals || []) {
    if (grouped[deal.pipeline_stage_id]) grouped[deal.pipeline_stage_id].push(deal);
  }
  dealsByStage.value = grouped;
}

// Trae SOLO las tarjetas (deals) y rellena las columnas ya pintadas. Guarda el
// resultado en la caché del embudo para la próxima entrada (Propuesta 2).
async function loadDeals(stages, { skipCache = false } = {}) {
  dealsLoading.value = true;
  try {
    const dealsRes = await DealsAPI.list({
      pipelineId: pipelineId.value,
      // Vista de chips de sucursal (Victor 2026-06-09): trae las 4 sucursales para
      // los 4 autorizados en Impresión/Álbum, así los chips filtran al instante.
      allBranches: canSeeBranchChips.value,
    });
    rebuildGroups(stages || pipeline.value?.stages, dealsRes.data.payload || []);
    // No cachear cuando es un override de tablero (otra sucursal): la caché debe
    // conservar SIEMPRE el estado de tu sucursal logeada (Victor 2026-06-10).
    if (!skipCache) {
      setKanbanCache(pipelineId.value, {
        pipeline: pipeline.value,
        dealsByStage: dealsByStage.value,
      });
    }
  } finally {
    dealsLoading.value = false;
  }
}

// Refresca embudo + tarjetas por detrás (cuando se mostró desde la caché).
async function refreshInBackground() {
  try {
    const pipelineRes = await PipelinesAPI.show(pipelineId.value);
    pipeline.value = pipelineRes.data;
    await loadDeals(pipelineRes.data.stages);
  } catch (_) {
    /* si falla el refresh, se conserva lo cacheado */
  }
}

async function loadData() {
  error.value = null;

  // Propuesta 2 · re-entrada instantánea: si ya cargamos este embudo antes,
  // mostramos lo cacheado de una vez y refrescamos en segundo plano.
  const cached = getKanbanCache(pipelineId.value);
  if (cached) {
    pipeline.value = cached.pipeline;
    dealsByStage.value = cached.dealsByStage;
    loading.value = false;
    refreshInBackground();
    return;
  }

  // Primera vez · Propuesta 1: pintamos las COLUMNAS al instante (llamada rápida)
  // y las tarjetas llegan después en segundo plano.
  loading.value = true;
  dealsByStage.value = {}; // limpia el embudo anterior
  try {
    const pipelineRes = await PipelinesAPI.show(pipelineId.value);
    pipeline.value = pipelineRes.data;
    rebuildGroups(pipelineRes.data.stages, []); // columnas vacías visibles ya
    loading.value = false; // ← el tablero se ve "de una vez"
    await loadDeals(pipelineRes.data.stages); // tarjetas en segundo plano
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
    loading.value = false;
  }
}

// ── Tablero: hasta 6 columnas visibles + navegación con mouse (Victor 2026-05-22) ──
// El ancho de columna se calcula del ancho real del tablero. Si el embudo tiene 6+
// etapas entran exactamente 6 (resto se ve arrastrando/rueda). Si tiene MENOS de 6,
// las columnas se ESTIRAN para llenar la pantalla — antes quedaban a ancho de 6 y
// dejaban un vacío negro feo a la derecha (Victor 2026-06-09). Fondo único: las
// columnas no llevan caja, solo título + línea de color.
const boardEl = ref(null);
const boardWidth = ref(0);
const VISIBLE_COLS = 6;
const COL_GAP_PX = 12; // gap-3
const BOARD_PADX_PX = 12; // px-3 (un lado)
const colWidth = computed(() => {
  const w = boardWidth.value;
  if (!w) return 280;
  // Cuántas columnas repartir el ancho: el menor entre 6 y las etapas reales, así
  // 3 etapas llenan la pantalla en vez de dejar la mitad en negro.
  const stageCount = pipeline.value?.stages?.length || VISIBLE_COLS;
  const cols = Math.min(VISIBLE_COLS, Math.max(1, stageCount));
  const inner = w - BOARD_PADX_PX * 2 - COL_GAP_PX * (cols - 1);
  return Math.max(160, Math.floor(inner / cols));
});

let resizeObs = null;
watch(boardEl, el => {
  if (resizeObs) {
    resizeObs.disconnect();
    resizeObs = null;
  }
  if (!el) return;
  boardWidth.value = el.clientWidth;
  resizeObs = new ResizeObserver(() => {
    boardWidth.value = el.clientWidth;
  });
  resizeObs.observe(el);
});

// Pan arrastrando el FONDO (no sobre una tarjeta ni el header reordenable).
let panning = false;
let panStartX = 0;
let panStartScroll = 0;
let panMoved = false;

function onBoardMouseDown(e) {
  if (e.button !== 0 || !boardEl.value) return;
  if (
    e.target.closest('.lc-card') ||
    e.target.closest('.stage-drag-handle') ||
    e.target.closest('button') ||
    e.target.closest('a') ||
    e.target.closest('input')
  ) {
    return;
  }
  panning = true;
  panMoved = false;
  panStartX = e.clientX;
  panStartScroll = boardEl.value.scrollLeft;
}
function onWinMouseMove(e) {
  if (!panning || !boardEl.value) return;
  const dx = e.clientX - panStartX;
  if (Math.abs(dx) > 3) panMoved = true;
  boardEl.value.scrollLeft = panStartScroll - dx;
  const colBody = document
    .elementFromPoint(e.clientX, e.clientY)
    ?.closest('.kb-colbody');
  if (colBody) colBody.scrollTop -= e.movementY || 0;
  if (panMoved) {
    boardEl.value.classList.add('kb-grabbing');
    e.preventDefault();
  }
}
function onWinMouseUp() {
  panning = false;
  boardEl.value?.classList.remove('kb-grabbing');
}
// Rueda → mueve el tablero en horizontal, salvo que la columna bajo el cursor
// aún tenga scroll vertical pendiente (muchas tarjetas).
function onBoardWheel(e) {
  if (!boardEl.value) return;
  const cb = e.target.closest('.kb-colbody');
  const colScrolls = cb && cb.scrollHeight > cb.clientHeight;
  if (colScrolls && Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
  e.preventDefault();
  boardEl.value.scrollLeft += e.deltaY || e.deltaX;
}

// "El cliente escribió" en vivo: cuando llega un mensaje, actualiza el preview
// de la tarjeta del deal ligado a esa conversación (Victor 2026-05-25).
function onDealMessage({ conversationId, content, messageType, createdAt }) {
  if (messageType === 2) return; // ignorar mensajes de actividad
  const byStage = dealsByStage.value || {};
  const stageId = Object.keys(byStage).find(sid =>
    (byStage[sid] || []).some(d => d.conversation_display_id === conversationId)
  );
  if (!stageId) return;
  const deal = byStage[stageId].find(
    d => d.conversation_display_id === conversationId
  );
  if (deal) {
    deal.last_message = {
      content: content || '',
      incoming: messageType === 0,
      created_at: createdAt,
    };
    // Mensaje entrante del cliente → incrementa el globo de no leídos en vivo.
    if (messageType === 0) {
      deal.unread_count = (deal.unread_count || 0) + 1;
    }
  }
}

onMounted(() => {
  loadData();
  window.addEventListener('mousemove', onWinMouseMove);
  window.addEventListener('mouseup', onWinMouseUp);
  emitter.on(BUS_EVENTS.NEW_MESSAGE_FOR_DEAL, onDealMessage);
});
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onWinMouseMove);
  window.removeEventListener('mouseup', onWinMouseUp);
  emitter.off(BUS_EVENTS.NEW_MESSAGE_FOR_DEAL, onDealMessage);
  if (resizeObs) resizeObs.disconnect();
});
watch(pipelineId, () => {
  // Al cambiar de embudo, reinicia el filtro de sucursal para que se vuelva a
  // preseleccionar (o se apague si el nuevo embudo no es Impresión/Álbum).
  branchFilterInitialized = false;
  activeBranchKey.value = null;
  loadData();
});

// ── Reordenar COLUMNAS (etapas) por drag del header · press-hold ──
const orderedStages = computed({
  get: () => pipeline.value?.stages || [],
  set: val => {
    if (pipeline.value) pipeline.value.stages = val;
  },
});

async function onStagesReordered() {
  const stages = orderedStages.value;
  try {
    await Promise.all(
      stages.map((s, idx) =>
        axios.patch(
          `/api/v1/accounts/${accountId.value}/pipelines/${pipelineId.value}/stages/${s.id}`,
          { stage: { position: idx } }
        )
      )
    );
  } catch (e) {
    // Best-effort: si falla la persistencia, recargar para resincronizar
    refreshInBackground();
  }
}

// Reordenar embudos en el switcher (drag & drop). Persiste position de cada
// pipeline · mismo patrón que onStagesReordered. Best-effort: recarga si falla.
async function onPipelinesReordered() {
  try {
    await Promise.all(
      allPipelines.value.map((p, idx) =>
        axios.patch(
          `/api/v1/accounts/${accountId.value}/pipelines/${p.id}`,
          { pipeline: { position: idx } }
        )
      )
    );
  } catch (e) {
    try {
      const { data } = await PipelinesAPI.get();
      allPipelines.value = data.payload || [];
    } catch (_) { /* noop */ }
  }
}

// Handler de vuedraggable @change. Se dispara con { added, removed, moved }.
// Solo nos interesa `added` (este stage recibió un deal de otra columna).
async function onDragChange(evt, targetStageId) {
  if (!evt.added) return;
  const deal = evt.added.element;
  const oldStageId = deal.pipeline_stage_id;
  if (oldStageId === targetStageId) return;

  // Optimistic UI: actualizar el deal localmente
  deal.pipeline_stage_id = targetStageId;
  deal.days_in_stage = 0;

  try {
    await DealsAPI.moveToStage(deal.id, targetStageId);
  } catch (e) {
    // Rollback: restaurar el deal a su stage original
    deal.pipeline_stage_id = oldStageId;
    // Quitar de la nueva columna y devolver a la original
    const idxNew = dealsByStage.value[targetStageId].findIndex(d => d.id === deal.id);
    if (idxNew >= 0) dealsByStage.value[targetStageId].splice(idxNew, 1);
    if (!dealsByStage.value[oldStageId]) dealsByStage.value[oldStageId] = [];
    dealsByStage.value[oldStageId].push(deal);
    // eslint-disable-next-line no-alert
    alert('Error moviendo lead: ' + (e.response?.data?.message || e.message));
  }
}

// ── Eliminar lead desde la tarjeta · menú ⋮ (Victor 2026-05-30) ──
// Solo SuperAdmin + Victor Manuel ven el botón (canDeleteLead arriba).
const openMenuDealId = ref(null); // id del deal con menú ⋮ abierto
const dealToDelete = ref(null); // deal en el modal de confirmación
const deleteStageId = ref(null); // stage de origen para optimistic + rollback
const deletingDeal = ref(false);

function toggleDealMenu(dealId, event) {
  event?.stopPropagation();
  openMenuDealId.value = openMenuDealId.value === dealId ? null : dealId;
}
function closeDealMenu() {
  openMenuDealId.value = null;
}
function askDeleteDeal(deal, stageId, event) {
  event?.stopPropagation();
  openMenuDealId.value = null;
  dealToDelete.value = deal;
  deleteStageId.value = stageId;
}
function cancelDeleteDeal() {
  dealToDelete.value = null;
  deleteStageId.value = null;
}
async function confirmDeleteDeal() {
  const deal = dealToDelete.value;
  const stageId = deleteStageId.value;
  if (!deal || deletingDeal.value) return;

  deletingDeal.value = true;
  // Optimistic removal · igual patrón que onDragChange
  const list = dealsByStage.value[stageId] || [];
  const idx = list.findIndex(d => d.id === deal.id);
  const removed = idx >= 0 ? list.splice(idx, 1)[0] : null;

  try {
    await DealsAPI.destroy(deal.id);
    dealToDelete.value = null;
    deleteStageId.value = null;
  } catch (e) {
    // Rollback si falla
    if (removed && idx >= 0) list.splice(idx, 0, removed);
    const code = e.response?.data?.error;
    const msg = code === 'lead_deleter_required'
      ? 'No tienes permiso para eliminar leads.'
      : (e.response?.data?.message || e.message);
    // eslint-disable-next-line no-alert
    alert('Error eliminando lead: ' + msg);
  } finally {
    deletingDeal.value = false;
  }
}

function goBack() {
  router.push({ name: 'pipelines_home' });
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

// Semáforo de días en la etapa (Victor 2026-05-24): verde 0-2d reciente,
// amarillo 3-7d atención, rojo 8d+ estancado. days_in_stage lo calcula el
// backend desde entered_stage_at y se reinicia al mover el lead de columna.
function stageDaysClass(deal) {
  const d = deal.days_in_stage || 0;
  if (d >= 8) return 'lc-days-stale';
  if (d >= 3) return 'lc-days-warn';
  return 'lc-days-fresh';
}

// Preview del último mensaje del cliente para el indicador "el cliente escribió".
function dealMsgPreview(deal) {
  const c = (deal.last_message?.content || '').trim();
  if (!c) return '📎 Adjunto';
  return c.length > 48 ? `${c.slice(0, 48)}…` : c;
}

// Parpadeo "latido" de atraso por embudo (Victor 2026-05-24): las tarjetas que
// superan el umbral de días en su etapa laten para resaltar el retraso. Se
// gatea por nombre del embudo (no por id, para que funcione en lab y prod):
//   · DTO. DE RETOQUE          → +10 días
//   · DTO. DE IMPRESIÓN        → +10 días
//   · DTO. DE ÁLBUM FOTOGRÁFICO → +45 días
// Cualquier otro embudo no parpadea.
const beatThreshold = computed(() => {
  const name = (pipeline.value?.name || '').toLowerCase();
  if (/retoque/.test(name)) return 10;
  if (/impresi[oó]n/.test(name)) return 10;
  if (/[aá]lbum/.test(name)) return 45;
  return null;
});
// Columnas tipo FIESTA (Victor 2026-05-30): se entregan en un ciclo distinto al
// resto del depto · NO laten ni cuentan como atraso, aunque pasen el umbral del
// embudo. Match por nombre del stage (case-insensitive).
const FIESTA_STAGE_REGEX = /fiesta/i;
function dealBeats(deal) {
  const threshold = beatThreshold.value;
  if (threshold == null) return false;
  if ((deal.days_in_stage || 0) <= threshold) return false;
  const stage = pipeline.value?.stages?.find(s => s.id === deal.pipeline_stage_id);
  // Etapas terminales (Victor 2026-06-02): LEAD LOGRADO (won) y LEAD PERDIDO
  // (lost) ya terminaron su ciclo → no laten ni avisan atraso aunque pasen días.
  if (stage && stage.stage_type !== 'open') return false;
  if (stage && FIESTA_STAGE_REGEX.test(stage.name || '')) return false;
  return true;
}

function avatarColor(id) {
  const colors = ['bg-pink-600', 'bg-purple-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600', 'bg-cyan-600', 'bg-rose-600'];
  return colors[id % colors.length];
}

// CM1 · Exportar leads de una columna a CSV (client-side, sin backend)
function exportColumnCsv(stage) {
  const deals = dealsByStage.value[stage.id] || [];
  if (!deals.length) return;

  const headers = ['Título', 'Nombre cliente', 'Teléfono', 'Email', 'Fecha creación', 'Días en columna'];
  const csvEscape = v => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = deals.map(d => [
    csvEscape(d.title || ''),
    csvEscape(d.contact?.name || ''),
    csvEscape(d.contact?.phone_number || ''),
    csvEscape(d.contact?.email || ''),
    csvEscape(formatDate(d.created_at)),
    csvEscape(d.days_in_stage || 0),
  ].join(','));
  const csv = '﻿' + headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeStage = (stage.name || 'leads').replace(/[^\w\d]/g, '-').toLowerCase();
  link.download = `leads-${safeStage}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// L4 · Channel Border · helpers para detectar canal/teléfono del deal
function looksLikePhone(str) {
  if (!str) return false;
  const digits = String(str).replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

function dealPhone(deal) {
  if (deal?.contact?.phone_number) return deal.contact.phone_number;
  // Fallback: si el title del deal parece un número de teléfono, usarlo
  if (looksLikePhone(deal?.title)) {
    const digits = String(deal.title).replace(/\D/g, '');
    return digits.startsWith('+') ? digits : `+${digits}`;
  }
  return '';
}

function dealDisplayName(deal) {
  if (deal?.contact?.name) return deal.contact.name;
  if (deal?.title && !looksLikePhone(deal.title)) return deal.title;
  if (deal?.title) return deal.title; // título numérico = mostrar igual
  return `Lead #${deal?.id || '?'}`;
}

function dealChannel(deal) {
  // 1) Canal REAL del inbox por donde llegó el lead (preferido · Victor 2026-05-31).
  const ct = deal?.inbox?.channel_type;
  const iname = (deal?.inbox?.name || '').toLowerCase();
  if (ct) {
    if (ct === 'Channel::Whatsapp') return { code: 'wa', label: 'WA', icon: '💬', cls: 'ch-wa' };
    if (ct === 'Channel::Instagram') return { code: 'ig', label: 'IG', icon: '📸', cls: 'ch-ig' };
    if (ct === 'Channel::FacebookPage') return { code: 'fb', label: 'FB', icon: 'f', cls: 'ch-fb' };
    if (ct === 'Channel::TwitterProfile' || ct === 'Channel::TwilioSms') return { code: 'tw', label: 'X', icon: '𝕏', cls: 'ch-tw' };
    if (ct === 'Channel::Email') return { code: 'email', label: 'EMAIL', icon: '✉', cls: 'ch-email' };
    if (ct === 'Channel::Api') {
      // Channel::Api se usa para WhatsApp (WAHA bridge) y TikTok (bridge propio).
      // Se distingue por el nombre del inbox.
      if (iname.includes('tiktok') || iname.includes(' tk ') || iname.startsWith('tk ')) {
        return { code: 'tk', label: 'TK', icon: '♪', cls: 'ch-tk' };
      }
      return { code: 'wa', label: 'WA', icon: '💬', cls: 'ch-wa' };
    }
  }
  // 2) Fallback: detección por social_profiles del contacto + teléfono/email.
  const sp = deal?.contact?.additional_attributes?.social_profiles || {};
  if (sp.instagram) return { code: 'ig', label: 'IG', icon: '📸', cls: 'ch-ig' };
  if (sp.facebook) return { code: 'fb', label: 'FB', icon: 'f', cls: 'ch-fb' };
  if (sp.twitter) return { code: 'tw', label: 'X', icon: '𝕏', cls: 'ch-tw' };
  if (deal?.contact?.phone_number || looksLikePhone(deal?.title)) {
    return { code: 'wa', label: 'WA', icon: '💬', cls: 'ch-wa' };
  }
  if (deal?.contact?.email) return { code: 'email', label: 'EMAIL', icon: '✉', cls: 'ch-email' };
  return { code: 'web', label: 'WEB', icon: '🌐', cls: 'ch-web' };
}

function dealHandle(deal) {
  const sp = deal?.contact?.additional_attributes?.social_profiles || {};
  if (sp.instagram) return `@${String(sp.instagram).replace(/^@/, '')}`;
  if (sp.facebook) return sp.facebook;
  if (deal?.contact?.email) return deal.contact.email;
  return '';
}

function onStageUpdated(updated) {
  const idx = pipeline.value.stages.findIndex(s => s.id === updated.id);
  if (idx >= 0) pipeline.value.stages[idx] = { ...pipeline.value.stages[idx], ...updated };
  editingStage.value = null;
}

function onStageDeleted(stageId) {
  pipeline.value.stages = pipeline.value.stages.filter(s => s.id !== stageId);
  delete dealsByStage.value[stageId];
  editingStage.value = null;
}

// Bandeja WhatsApp conectada de una sucursal (WAHA conectada primero · mismo
// criterio que NewDealDialog/LeadCardPanel). Para escribirle a un lead que
// todavía no tiene conversación abierta.
function pickConnectedInboxForBranch(branchId) {
  const wa = ['Channel::Api', 'Channel::Whatsapp'];
  const list = (store.getters.getInboxes || []).filter(i =>
    wa.includes(i.channel_type)
  );
  const rank = i => {
    const wh = String(i.webhook_url || '').toLowerCase();
    const conn = i.additional_attributes?.conn_status === 'connected';
    const isWaha = wh.includes('waha');
    if (isWaha && conn) return 0;
    if (conn) return 1;
    if (isWaha) return 2;
    return 3;
  };
  const inBranch = branchId
    ? list.filter(i => i.branch_id === branchId)
    : list;
  const pool = inBranch.length ? inBranch : list;
  return (
    [...pool]
      .sort((a, b) => rank(a) - rank(b))
      .find(
        i =>
          i.channel_type !== 'Channel::Api' ||
          i.additional_attributes?.conn_status === 'connected'
      ) || null
  );
}

async function openDealDetail(deal) {
  // Acceso restringido: usuarios no-allowlist saltan a detalle del deal sin abrir chat.
  if (!canSeeChat.value) {
    router.push({
      name: 'pipelines_deal',
      params: {
        accountId: accountId.value,
        pipelineId: pipelineId.value,
        dealId: deal.id,
      },
    });
    return;
  }
  // Regla universal para todos los embudos: click en tarjeta del kanban abre
  // el chat del cliente, donde se ve la tarjeta nueva (Glass M2 + path) y se
  // puede responder al instante.
  // Abrir SIEMPRE la conversación de la SUCURSAL de la agente (Victor 2026-06-03):
  // si el cliente escribió a varias sucursales, cada agente abre la de la SUYA.
  // Las bandejas de otras sucursales ni están en su store → con eso filtramos.
  const myInboxIds = new Set((store.getters.getInboxes || []).map(i => i.id));
  const activeBranchId = store.getters['branches/getActiveBranchId'] || null;

  // 1) Si el deal ya está ligado a una conversación de SU sucursal, úsala directo.
  if (
    deal.conversation_id &&
    (!activeBranchId || deal.branch_id === activeBranchId)
  ) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: deal.conversation_id },
    });
    return;
  }
  // 2) Buscar la conversación del contacto EN la sucursal de la agente (más
  //    reciente de ESA sucursal). Nunca abrir la de otra sucursal.
  if (deal.contact_id) {
    try {
      const { data } = await ContactAPI.getConversations(deal.contact_id);
      const convs = data?.payload || data || [];
      const mine = convs.filter(c => myInboxIds.has(c.inbox_id));
      // Preferir la conversación de SU sucursal; pero si el cliente no tiene
      // ninguna en mis bandejas (otra sucursal, o inboxes aún sin cargar),
      // abrir IGUAL la más reciente del contacto — nunca caer a la ficha
      // (Victor 2026-06-03 · clic en lead = entrar al chat para escribir).
      const pool = mine.length > 0 ? mine : convs;
      if (pool.length > 0) {
        const sorted = [...pool].sort(
          (a, b) =>
            (b.last_activity_at || b.created_at || 0) -
            (a.last_activity_at || a.created_at || 0)
        );
        router.push({
          name: 'inbox_conversation',
          params: { conversation_id: sorted[0].id },
        });
        return;
      }
    } catch (e) {
      // silent — caemos al fallback (crear nueva en su sucursal)
    }
  }
  // 3) Sin conversación: crear una en la bandeja WhatsApp conectada de la
  //    sucursal del lead y abrirla, listo para escribir (reusa plomería del
  //    selector de número · funciona en cualquier etapa).
  const phoneDigits = String(deal.contact?.phone_number || '').replace(/\D/g, '');
  if (deal.contact_id && phoneDigits.length >= 8) {
    // Si viene de "Nuevo lead", usa la bandeja que el usuario eligió en el
    // modal (más confiable que adivinar la conectada · funciona en lab).
    const inbox = deal._newLeadInboxId
      ? (store.getters.getInboxes || []).find(i => i.id === deal._newLeadInboxId)
      : pickConnectedInboxForBranch(deal.branch_id);
    if (inbox) {
      try {
        const data = await store.dispatch('contactConversations/create', {
          isFromWhatsApp: true,
          params: {
            inboxId: inbox.id,
            contactId: deal.contact_id,
            sourceId: `${phoneDigits}@c.us`,
          },
        });
        if (data?.id) {
          router.push({
            name: 'inbox_conversation',
            params: { conversation_id: data.id },
          });
          return;
        }
      } catch (e) {
        // si falla, caemos al detalle del deal
      }
    }
  }

  // 4) Fallback (raro: sin teléfono y sin conversación): abrir la ficha del
  //    contacto (vista moderna) — NUNCA el modal minimalista de detalle del deal.
  if (deal.contact_id) {
    router.push(
      `/app/accounts/${accountId.value}/contacts/${deal.contact_id}`
    );
    return;
  }
  router.push({ name: 'home', params: { accountId: accountId.value } });
}

function openNewDealDialog(stageId = null) {
  newDealStageId.value = stageId;
  showNewDealDialog.value = true;
}

function onDealCreated(deal, opts = {}) {
  if (!dealsByStage.value[deal.pipeline_stage_id]) {
    dealsByStage.value[deal.pipeline_stage_id] = [];
  }
  dealsByStage.value[deal.pipeline_stage_id].unshift(deal);
  showNewDealDialog.value = false;
  newDealStageId.value = null;
  // "Crear y abrir chat": deja al agente escribiéndole de una vez (crea la
  // conversación en la WAHA conectada de la sucursal si no existe).
  if (opts.openChat) openDealDetail(deal);
}

function onStageCreated(stage) {
  pipeline.value.stages.push(stage);
  dealsByStage.value[stage.id] = [];
  showNewStageDialog.value = false;
}
</script>

<template>
  <div class="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
    <!-- ===== T2 · Toolbar dual rows (Kommo-style M1.B) ===== -->
    <div v-on-click-outside="closePipelineSwitcher" class="t2-toolbar">
      <!-- ROW 1: identidad embudo + stats + CTA -->
      <div class="t2-row-top">
        <div class="t2-pipeline-large-wrap">
          <button
            type="button"
            class="t2-pipeline-large"
            :class="{ 'is-open': showPipelineSwitcher }"
            @click="togglePipelineSwitcher"
          >
            <div class="t2-pipeline-icon-lg">{{ pipelineIcon() }}</div>
            <div class="t2-pipeline-info">
              <div class="t2-pipeline-name">
                {{ pipeline?.name || 'Cargando…' }}
                <span class="chev" :style="{ transform: showPipelineSwitcher ? 'rotate(180deg)' : 'rotate(0)' }">▼</span>
              </div>
              <div class="t2-pipeline-tagline">
                {{ pipeline?.stages?.length || 0 }} etapas · cambiar embudo
              </div>
            </div>
          </button>
        </div>

        <div class="t2-stats-row">
          <div class="t2-stat-card">
            <div class="t2-stat-label">Leads</div>
            <div class="t2-stat-value">
              <span v-if="dealsLoading && !totalVisibleLeads" class="kb-cards-loading" title="Cargando tarjetas…">⟳</span>
              <span v-else>{{ totalVisibleLeads }}</span>
            </div>
          </div>
          <div class="t2-stat-card">
            <div class="t2-stat-label">Monto</div>
            <div class="t2-stat-value gold">{{ formatAmountShort(totalVisibleAmount) }}</div>
          </div>
          <div class="t2-stat-card">
            <div class="t2-stat-label">Etapas</div>
            <div class="t2-stat-value">{{ pipeline?.stages?.length || 0 }}</div>
          </div>
        </div>

<!-- "+ Nuevo lead" removido del toolbar (cada columna tiene su propio "+ Agregar lead") -->
      </div>

      <!-- ROW 2: vista + chips filtro + búsqueda + gear -->
      <div class="t2-row-bottom">
        <div class="t2-view-toggle">
          <button class="t2-view-btn" :class="{ active: currentView === 'kanban' }" @click="currentView = 'kanban'" title="Vista Kanban">▦</button>
          <button class="t2-view-btn" :class="{ active: currentView === 'lista', 'is-disabled': true }" disabled title="Vista Lista (próximamente)">☰</button>
        </div>

        <!-- Estados en control segmentado (Propuesta 1 · Victor 2026-06-11): más
             fino y profesional · "En atraso" (rojo) reemplaza "Sin asignar". -->
        <div class="t2-seg">
          <button
            v-if="atrasadosIds"
            class="t2-seg-btn t2-seg-atraso on"
            title="Mostrando solo los clientes en atraso · clic para quitar"
            @click="clearAtrasados"
          >🚨 En atraso ({{ atrasadosIds.size }}) ✕</button>
          <button class="t2-seg-btn t2-seg-abiertos" :class="{ on: activeFilter === 'abiertos' }" @click="setFilter('abiertos')">Abiertos</button>
          <button class="t2-seg-btn" :class="{ on: activeFilter === 'mios' }" @click="setFilter('mios')">Míos</button>
          <button class="t2-seg-btn" :class="{ on: activeFilter === 'urgentes' }" @click="setFilter('urgentes')">🔥 Urgentes</button>
          <button
            v-if="!atrasadosIds"
            class="t2-seg-btn t2-seg-atraso"
            :class="{ on: activeFilter === 'atraso' }"
            @click="setFilter('atraso')"
          >🚨 En atraso</button>
        </div>

        <!-- Chips de sucursal (Victor 2026-06-09): un clic = solo los clientes de
             esa sucursal en este departamento. Solo los 4 autorizados, en
             Impresión y Álbum. -->
        <template v-if="canSeeBranchChips">
          <div class="t2-branch-divider"></div>
          <div class="t2-chips">
            <button
              v-for="b in branchChips"
              :key="b.key"
              class="t2-chip t2-chip-branch"
              :class="{ active: activeBranchKey === b.key }"
              :style="activeBranchKey === b.key ? { borderColor: b.color, color: b.color } : {}"
              :title="`Ver solo clientes de ${b.label}`"
              @click="toggleBranch(b.key)"
            ><span class="t2-branch-dot" :style="{ backgroundColor: b.color }"></span>{{ b.label }}</button>
          </div>
        </template>

        <div class="t2-spacer"></div>

        <div v-on-click-outside="() => { globalResults = []; }" class="t2-search t2-search--compact" style="position: relative;">
          <span class="t2-search-icon">🔍</span>
          <input
            v-model="searchQuery"
            type="text"
            class="reset-base"
            placeholder="Buscar leads, agentes, etapas… o pregunta natural"
          />
          <span class="t2-search-ai-badge">✨ IA</span>
          <span class="t2-search-kbd"><kbd>⌘K</kbd></span>

          <div
            v-if="searchQuery.trim().length >= 2"
            class="ksr-panel"
          >
            <div v-if="searchingGlobal" class="ksr-empty">Buscando…</div>
            <template v-else>
              <div v-if="globalResults.length === 0" class="ksr-empty">
                Sin resultados para “{{ searchQuery.trim() }}”
              </div>
              <template v-else>
                <div class="ksr-head">
                  {{ globalResults.length }} {{ globalResults.length === 1 ? 'lead' : 'leads' }} · en todos los embudos · más avanzado primero
                </div>
                <button
                  v-for="(d, i) in globalResults"
                  :key="d.id"
                  type="button"
                  class="ksr-row"
                  @click="openSearchResult(d)"
                >
                  <span class="ksr-rank">{{ i + 1 }}</span>
                  <span class="ksr-av">{{ (d.contact?.name || d.title || '?').trim().charAt(0).toUpperCase() }}</span>
                  <span class="ksr-info">
                    <span class="ksr-name">{{ d.contact?.name || d.title || 'Lead' }}</span>
                    <span class="ksr-sub">{{ d.contact?.phone_number || d.contact?.email || '' }}</span>
                    <span class="ksr-tags">
                      <!-- Sucursal del resultado (Victor 2026-06-09 · Propuesta 1):
                           chip de color por sucursal para identificarla al instante. -->
                      <span
                        v-if="branchLabel(d.branch_id)"
                        class="ksr-branch-chip"
                        :class="branchClass(d.branch_id)"
                      >📍 {{ branchLabel(d.branch_id) }}</span>
                      <span class="ksr-pipe">{{ d.pipeline?.name || '—' }}</span>
                      <span
                        v-if="d.pipeline_stage?.name"
                        class="ksr-stage-chip"
                        :style="ksrStageStyle(d.pipeline_stage)"
                      >{{ d.pipeline_stage.name }}</span>
                    </span>
                  </span>
                  <span class="ksr-right">
                    <span v-if="dealAmount(d)" class="ksr-amt">{{ dealAmount(d) }}</span>
                    <span v-if="d.assignee?.name" class="ksr-assignee">{{ d.assignee.name }}</span>
                    <span class="ksr-go">Ir →</span>
                  </span>
                </button>
              </template>
            </template>
          </div>
        </div>

        <div v-on-click-outside="closeGearMenu" class="t2-gear-wrap">
          <button class="t2-gear" :class="{ active: showGearMenu }" @click="toggleGearMenu" title="Configuración">⚙</button>

          <div v-if="showGearMenu" class="t2-gear-menu">
            <div class="t2-gear-menu-header">Configuración</div>
            <div class="t2-gear-menu-inner">
              <button class="t2-gear-item" @click="showNewStageDialog = true; closeGearMenu()">
                <span class="ico">＋</span>
                <span>Agregar columna</span>
              </button>
              <div class="t2-gear-divider"></div>
              <button class="t2-gear-item gold" @click="openDisparadoresModal">
                <span class="ico">⚡</span>
                <span>Agregar disparador</span>
              </button>
              <!-- Editar/Eliminar/Crear embudo · respeta permisos granulares
                   (Victor 2026-05-30): admin/super ven todo; agentes con preset
                   Selección ven solo lo que el permiso les concede. Antes era
                   v-if="isAdminOrSuperAdmin" que bloqueaba a Elinne/Mayerlyn. -->
              <template v-if="pipeline && (isAdminOrSuperAdmin || can('pipelines.edit') || can('pipelines.delete') || can('pipelines.create'))">
                <div class="t2-gear-divider"></div>
                <button
                  v-if="isAdminOrSuperAdmin || can('pipelines.edit')"
                  class="t2-gear-item gold"
                  @click="closeGearMenu(); openEditPipelineDialog();"
                >
                  <span class="ico">✏</span>
                  <span>Editar embudo actual</span>
                </button>
                <button
                  v-if="isAdminOrSuperAdmin || can('pipelines.delete')"
                  class="t2-gear-item danger"
                  @click="closeGearMenu(); openDeletePipelineDialog();"
                >
                  <span class="ico">🗑</span>
                  <span>Eliminar embudo</span>
                </button>
                <template v-if="isAdminOrSuperAdmin || can('pipelines.create')">
                  <div class="t2-gear-divider"></div>
                  <button class="t2-gear-item gold" @click="closeGearMenu(); openNewPipelineDialog();">
                    <span class="ico">＋</span>
                    <span>Crear nuevo embudo</span>
                  </button>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Dropdown pipeline switcher (estilo C4 cristal negro · preservado) -->
      <div
        v-if="showPipelineSwitcher"
        class="absolute z-50 min-w-[340px] rounded-[14px] overflow-hidden p-1.5"
        style="
          top: calc(100% + 6px); left: 18px;
          background: rgba(10,10,10,0.92);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          border: 1.5px solid rgba(255,255,255,0.15);
          box-shadow: 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 28px rgba(255,255,255,0.08), 0 24px 60px rgba(0,0,0,0.7);
        "
      >
          <!-- Capa de facetas + radial blanco -->
          <div
            class="absolute inset-0 pointer-events-none rounded-[14px]"
            style="background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.12) 22%, transparent 24%), linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.08) 50%, transparent 52%), radial-gradient(circle at 100% 0%, rgba(255,255,255,0.15), transparent 65%); z-index: 1;"
          ></div>

          <!-- Buscador (cuando hay 5+ pipelines) -->
          <div
            v-if="allPipelines.length >= 5"
            class="relative flex items-center gap-2 px-3 py-2 rounded-lg mb-1"
            style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); z-index: 2;"
          >
            <span class="text-white/40 text-[11px]">🔍</span>
            <input
              v-model="pipelineSearchQuery"
              type="text"
              placeholder="Buscar embudo…"
              class="flex-1 bg-transparent outline-none text-[12px] text-white"
              @click.stop
            />
          </div>

          <!-- Filas de pipelines · arrastra ⠿ para reordenar (se guarda solo) -->
          <Draggable
            class="relative"
            style="z-index: 2;"
            v-model="allPipelines"
            item-key="id"
            handle=".pl-drag-handle"
            :disabled="!!pipelineSearchQuery.trim()"
            @end="onPipelinesReordered"
          >
            <template #item="{ element: p }">
              <button
                v-show="!pipelineSearchQuery.trim() || (p.name || '').toLowerCase().includes(pipelineSearchQuery.trim().toLowerCase())"
                :key="p.id"
                type="button"
                class="w-full flex items-center gap-3 px-3.5 py-3 rounded-[10px] cursor-pointer transition-colors text-left"
                :class="p.id === pipelineId
                  ? ''
                  : 'hover:bg-white/5'"
                :style="p.id === pipelineId
                  ? 'background: rgba(255,255,255,0.08); box-shadow: inset 0 0 0 1px rgba(255,255,255,0.2);'
                  : ''"
                @click="selectPipeline(p)"
              >
                <span
                  class="pl-drag-handle text-white/35 text-[15px] flex-shrink-0 cursor-grab select-none"
                  style="letter-spacing: -2px;"
                  title="Arrastra para reordenar"
                  @click.stop
                >⠿</span>
                <span
                  class="w-9 h-9 rounded-[9px] flex items-center justify-center text-[14px] font-bold flex-shrink-0 border"
                  :style="p.id === pipelineId
                    ? 'background: linear-gradient(135deg,#fff,#cbd5e1); color: #18181b; border-color: #fff;'
                    : 'background: rgba(255,255,255,0.08); color: #e2e8f0; border-color: rgba(255,255,255,0.15);'"
                >{{ pipelineInitial(p) }}</span>
                <span class="flex-1 min-w-0">
                  <span class="block text-[13px] font-bold text-white truncate leading-tight">{{ p.name }}</span>
                  <span class="block text-[10px] text-white/60 mt-0.5">{{ p.stages?.length || 0 }} etapas</span>
                </span>
                <span v-if="p.id === pipelineId" class="text-white text-[14px] font-extrabold">✓</span>
              </button>
            </template>
          </Draggable>
          <div v-if="filteredPipelines.length === 0" class="relative px-3 py-4 text-[11px] text-white/50 italic text-center" style="z-index: 2;">
            Sin resultados
          </div>

          <!-- Divider + botón "✏ Editar embudo actual" + "+ Crear nuevo embudo" -->
          <div v-if="isAdminOrSuperAdmin" class="relative my-1.5 mx-1" style="z-index: 2; height: 1px; background: rgba(255,255,255,0.08);"></div>
          <button
            v-if="isAdminOrSuperAdmin && pipeline"
            type="button"
            class="relative w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] cursor-pointer transition-colors text-left"
            style="z-index: 2; color: #fbbf24;"
            :class="'hover:bg-yellow-500/10'"
            @click="openEditPipelineDialog"
          >
            <span
              class="w-9 h-9 rounded-[9px] flex items-center justify-center text-[14px] flex-shrink-0 border"
              style="background: rgba(251,191,36,0.10); color: #fbbf24; border-color: rgba(251,191,36,0.35);"
            >✏</span>
            <span class="flex-1 min-w-0">
              <span class="block text-[13px] font-bold leading-tight">Editar embudo actual</span>
              <span class="block text-[10px] text-white/50 mt-0.5">{{ pipeline?.name }} · sucursales y nombre</span>
            </span>
          </button>
          <button
            v-if="isAdminOrSuperAdmin && pipeline"
            type="button"
            class="relative w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] cursor-pointer transition-colors text-left"
            style="z-index: 2; color: #f87171;"
            :class="'hover:bg-red-500/10'"
            @click="openDeletePipelineDialog"
          >
            <span
              class="w-9 h-9 rounded-[9px] flex items-center justify-center text-[14px] flex-shrink-0 border"
              style="background: rgba(239,68,68,0.10); color: #f87171; border-color: rgba(239,68,68,0.35);"
            >🗑</span>
            <span class="flex-1 min-w-0">
              <span class="block text-[13px] font-bold leading-tight">Eliminar embudo actual</span>
              <span class="block text-[10px] text-white/50 mt-0.5">De mi sucursal, algunas o todas</span>
            </span>
          </button>
          <template v-if="isAdminOrSuperAdmin">
            <button
              type="button"
              class="relative w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] cursor-pointer transition-colors text-left"
              style="z-index: 2; color: #fbbf24;"
              :class="'hover:bg-yellow-500/10'"
              @click="openNewPipelineDialog"
            >
              <span
                class="w-9 h-9 rounded-[9px] flex items-center justify-center text-[20px] font-extralight flex-shrink-0 border"
                style="background: rgba(251,191,36,0.10); color: #fbbf24; border-color: rgba(251,191,36,0.35);"
              >+</span>
              <span class="flex-1 min-w-0">
                <span class="block text-[13px] font-bold leading-tight">Crear nuevo embudo</span>
                <span class="block text-[10px] text-white/50 mt-0.5">Departamento o pipeline custom</span>
              </span>
            </button>
          </template>
      </div>
    </div>

    <div v-if="loading" class="p-6 text-sm text-slate-500">Cargando kanban…</div>
    <div v-else-if="error" class="m-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300 text-sm">
      Error al cargar: {{ error }}
    </div>

    <main
      v-else
      ref="boardEl"
      class="kb-board flex-1 overflow-x-auto overflow-y-hidden px-3 pt-5 pb-4 bg-black"
      @mousedown="onBoardMouseDown"
      @wheel="onBoardWheel"
    >
      <Draggable
        v-model="orderedStages"
        item-key="id"
        class="flex gap-3 h-full"
        handle=".stage-drag-handle"
        :group="{ name: 'pipeline-stages' }"
        :delay="140"
        :delay-on-touch-only="false"
        :touch-start-threshold="6"
        force-fallback
        :fallback-tolerance="4"
        :animation="180"
        :disabled="!(isAdminOrSuperAdmin || can('stages.edit'))"
        @end="onStagesReordered"
      >
        <template #item="{ element: stage }">
        <div
          class="kb-col flex flex-col"
          :class="openPanelStageId === stage.id ? 'relative z-10' : 'overflow-hidden'"
          :style="{ width: colWidth + 'px', minWidth: colWidth + 'px' }"
        >
          <!-- Header F4 (Victor 2026-05-20): neutro · color de la etapa como
               underline degradado bajo el nombre · sin glass dorado, sin stripe. -->
          <div
            class="relative stage-drag-handle"
            :class="(isAdminOrSuperAdmin || can('stages.edit')) ? 'cursor-move' : ''"
          >
            <div class="flex items-center justify-between px-4 pt-3.5 pb-2">
              <span
                class="text-[11px] font-extrabold uppercase text-white/90 truncate flex-1 mr-2"
                style="letter-spacing: 1.2px;"
              >{{ stage.name }}</span>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span
                  class="px-2.5 py-0.5 rounded-full text-[10.5px] font-bold leading-none text-white/55 bg-white/5 border border-white/10"
                >{{ visibleCountsByStage[stage.id] || 0 }}</span>
                <button
                  v-if="isAdminOrSuperAdmin || can('stages.edit')"
                  @click.stop="openPanelStageId = (openPanelStageId === stage.id ? null : stage.id)"
                  class="p-1 rounded transition text-white/35 hover:text-white/70"
                  :class="openPanelStageId === stage.id ? '!text-white/80' : ''"
                  title="Gestionar columna"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/></svg>
                </button>
              </div>
            </div>
            <!-- underline degradado del color de la etapa, bajo el nombre -->
            <div
              class="mx-4 mb-2.5 h-[2px] rounded-full"
              :style="{ background: `linear-gradient(90deg, ${stage.color}, transparent)` }"
            />
          </div>

          <!-- Wrapper que scrollea cards + botón juntos para que "+ Agregar
               lead" quede justo bajo la última tarjeta (Victor 2026-05-21). -->
          <div class="kb-colbody flex-1 overflow-y-auto px-2 py-2">
          <Draggable
            v-model="dealsByStage[stage.id]"
            :group="{ name: 'deals', pull: true, put: true }"
            item-key="id"
            class="space-y-2.5 min-h-[60px]"
            ghost-class="opacity-30"
            drag-class="rotate-2"
            :animation="180"
            :disabled="false"
            :delay="140"
            :delay-on-touch-only="false"
            :touch-start-threshold="6"
            force-fallback
            :fallback-tolerance="4"
            fallback-class="rotate-2"
            @change="(evt) => onDragChange(evt, stage.id)"
          >
            <template #item="{ element: deal }">
              <div
                v-show="isDealVisible(deal)"
                @click="openDealDetail(deal)"
                class="lc-card"
                :class="[
                  canSeeChat ? `lc-ch-${dealChannel(deal).code}` : 'lc-ch-web',
                  { 'lc-beat': dealBeats(deal) },
                ]"
              >
                <!-- Badge días en la etapa · semáforo (P2) -->
                <span class="lc-days-badge" :class="stageDaysClass(deal)">
                  ● {{ deal.days_in_stage || 0 }}d
                </span>
                <!-- Menú ⋮ Eliminar lead · solo SuperAdmin + Victor Manuel (Victor 2026-05-30) -->
                <div
                  v-if="canDeleteLead"
                  class="lc-kebab-wrap"
                  v-on-click-outside="() => { if (openMenuDealId === deal.id) closeDealMenu(); }"
                >
                  <button
                    class="lc-kebab"
                    :class="{ 'lc-kebab-open': openMenuDealId === deal.id }"
                    @click="toggleDealMenu(deal.id, $event)"
                    title="Más opciones"
                  >⋮</button>
                  <div
                    v-if="openMenuDealId === deal.id"
                    class="lc-menu"
                    @click.stop
                  >
                    <button
                      class="lc-menu-item lc-menu-item-danger"
                      @click="askDeleteDeal(deal, stage.id, $event)"
                    >
                      <span class="lc-menu-ic">🗑</span>
                      <span class="lc-menu-tx">
                        <span class="lc-menu-tx-main">Eliminar lead</span>
                        <span class="lc-menu-tx-sub">Solo el deal · contacto y otros embudos quedan</span>
                      </span>
                    </button>
                  </div>
                </div>
                <!-- Avatar 32px (L4) · wrap para badge de canal en esquina (Victor 2026-05-31) -->
                <div class="lc-avatar-wrap">
                  <template v-if="canSeeChat">
                    <img
                      v-if="deal.contact?.thumbnail"
                      :src="deal.contact.thumbnail"
                      class="lc-avatar-img"
                      :alt="deal.contact.name"
                    />
                    <div
                      v-else
                      class="lc-avatar"
                      :class="avatarColor(deal.contact?.id || deal.id)"
                    >
                      {{ getInitials(deal.contact?.name || deal.title) }}
                    </div>
                    <!-- Badge canal · esquina inferior derecha (P1 monocromo M1.B) -->
                    <span
                      class="lc-ch-badge"
                      :class="`lc-ch-badge-${dealChannel(deal).code}`"
                      :title="dealChannel(deal).label"
                    >
                      <svg v-if="dealChannel(deal).code === 'wa'" width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.6-1.9-.2-.3 0-.4.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5C9.9 9.2 9.3 7.7 9 7c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.5-.3z"/></svg>
                      <svg v-else-if="dealChannel(deal).code === 'ig'" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17" cy="7" r="1" fill="currentColor"/></svg>
                      <svg v-else-if="dealChannel(deal).code === 'fb'" width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.95.93-1.95 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
                      <svg v-else-if="dealChannel(deal).code === 'tk'" width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.07a8.16 8.16 0 0 0 4.77 1.52V7.15a4.85 4.85 0 0 1-1.84-.46z"/></svg>
                    </span>
                  </template>
                  <div v-else class="lc-avatar lc-avatar-neutral">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                </div>

                <div class="lc-info">
                  <div class="lc-row1">
                    <div class="lc-name">
                      {{ canSeeChat ? dealDisplayName(deal) : `Lead #${deal.id}` }}
                    </div>
                    <!-- Icono de canal oculto (Victor 2026-05-25): no le gusta el
                         cuadradito verde 💬 junto al nombre. -->
                  </div>
                  <div class="lc-meta">
                    <div v-if="canSeeChat && dealPhone(deal)" class="lc-meta-row">📱 {{ dealPhone(deal) }}</div>
                    <div v-if="canSeeChat && dealHandle(deal)" class="lc-meta-row">{{ dealHandle(deal) }}</div>
                  </div>
                  <!-- "El cliente escribió" sin leer · burbuja azul + globo 💬 N
                       (variante B · Victor 2026-05-25). Igual que la lista de chats. -->
                  <div
                    v-if="canSeeChat && (deal.unread_count || 0) > 0"
                    class="lc-unread"
                  >
                    <span class="lc-unread-pill">
                      <span class="lc-unread-ico">💬</span>
                      <span class="lc-unread-tx">{{ dealMsgPreview(deal) }}</span>
                    </span>
                    <span class="lc-unread-cnt">💬 {{ deal.unread_count }}</span>
                  </div>
                  <div class="lc-foot">
                    <span>📩 Creado {{ formatDate(deal.created_at) }}</span>
                  </div>
                </div>
              </div>
            </template>
          </Draggable>

          <div
            v-if="(visibleCountsByStage[stage.id] || 0) === 0"
            class="text-[11px] text-slate-400 italic text-center pb-3"
          >
            <template v-if="dealsLoading && !totalVisibleLeads">Cargando…</template>
            <template v-else-if="(dealsByStage[stage.id]?.length || 0) === 0">Suelta un lead aquí</template>
            <template v-else>Sin resultados con el filtro</template>
          </div>

          <button
            @click="openNewDealDialog(stage.id)"
            class="block w-full mt-1.5 text-[11px] text-white/25 hover:text-white/55 hover:bg-white/[0.03] rounded py-1.5 transition-colors"
          >+ Agregar lead</button>
          </div>

          <!-- CM1 · Panel lateral de gestión de columna -->
          <ColumnSidePanel
            v-if="openPanelStageId === stage.id"
            :stage="stage"
            :pipeline="pipeline"
            :all-stages="pipeline?.stages || []"
            :account-id="accountId"
            :total-leads="dealsByStage[stage.id]?.length || 0"
            @close="openPanelStageId = null"
            @edit="editingStage = stage"
            @delete="deletingStage = stage"
            @reordered="refreshInBackground"
            @import-csv="importingStage = stage"
            @export-csv="exportColumnCsv(stage)"
          />
        </div>
        </template>
      </Draggable>
    </main>

    <footer class="border-t border-slate-200 dark:border-slate-800 px-6 py-2 text-[11px] text-slate-500 shrink-0">
      ✨ Drag-and-drop entre columnas · Click en card para abrir detalle + chat · Arrastra el fondo o usa la rueda para moverte · Click en ⋮ de columna para editar
    </footer>

    <EditStageDialog
      :stage="editingStage"
      :pipeline-id="pipelineId"
      :account-id="accountId"
      :pipeline="pipeline"
      :deals-count="editingStage ? (dealsByStage[editingStage.id]?.length || 0) : 0"
      @close="editingStage = null"
      @updated="onStageUpdated"
      @deleted="onStageDeleted"
    />

    <DeleteStageDialog
      v-if="deletingStage && pipeline"
      :stage="deletingStage"
      :pipeline="pipeline"
      :pipeline-id="pipelineId"
      :account-id="accountId"
      :deals-in-stage="deletingStage ? (dealsByStage[deletingStage.id]?.length || 0) : 0"
      @close="deletingStage = null"
      @deleted="(s) => { onStageDeleted(s); deletingStage = null; }"
      @updated="onStageUpdated"
    />

    <ImportLeadsCsvDialog
      v-if="importingStage && pipeline"
      :stage="importingStage"
      :pipeline="pipeline"
      :account-id="accountId"
      @close="importingStage = null"
      @imported="refreshInBackground"
    />

    <NewDealDialog
      v-if="showNewDealDialog && pipeline"
      :pipeline="pipeline"
      :default-stage-id="newDealStageId"
      :account-id="accountId"
      @close="showNewDealDialog = false; newDealStageId = null"
      @created="onDealCreated"
    />

    <NewStageDialog
      v-if="showNewStageDialog"
      :pipeline-id="pipelineId"
      :account-id="accountId"
      :pipeline="pipeline"
      @close="showNewStageDialog = false"
      @created="onStageCreated"
    />

    <NewPipelineDialog
      v-if="showNewPipelineDialog"
      :account-id="accountId"
      @close="showNewPipelineDialog = false"
      @created="onPipelineCreated"
    />

    <EditPipelineDialog
      v-if="showEditPipelineDialog && pipeline"
      :account-id="accountId"
      :pipeline="pipeline"
      @close="showEditPipelineDialog = false"
      @updated="onPipelineUpdated"
    />

    <DeletePipelineDialog
      v-if="showDeletePipelineDialog && pipeline"
      :account-id="accountId"
      :pipeline="pipeline"
      @close="showDeletePipelineDialog = false"
      @updated="onPipelineUpdated"
      @deleted="onPipelineDeleted"
    />

    <DisparadoresModal
      v-if="showDisparadoresModal && pipeline"
      :pipeline="pipeline"
      :account-id="accountId"
      @close="showDisparadoresModal = false"
    />

    <!-- Modal confirmar eliminar lead (Victor 2026-05-30) · estilo glass M1.B -->
    <div v-if="dealToDelete" class="del-overlay" @click.self="cancelDeleteDeal">
      <div class="del-modal">
        <div class="del-top"></div>
        <div class="del-body">
          <div class="del-ico">🗑</div>
          <div class="del-ttl">¿Eliminar este lead?</div>
          <div class="del-desc">
            Se borrará <b>solo el deal</b> de este embudo. No se elimina el contacto ni los otros embudos donde aparece esta clienta.
          </div>
          <div class="del-leadbox">
            <div class="del-leadbox-name">{{ dealDisplayName(dealToDelete) }}</div>
            <div class="del-leadbox-sub">
              {{ dealPhone(dealToDelete) || `Lead #${dealToDelete.id}` }} · Creado {{ formatDate(dealToDelete.created_at) }}
            </div>
          </div>
          <div class="del-keep">
            <div class="del-keep-lbl">✓ Se conserva</div>
            <div class="del-keep-li">El contacto y todos sus chats</div>
            <div class="del-keep-li">Otros embudos donde aparece</div>
          </div>
          <div class="del-actions">
            <button
              class="del-btn del-btn-cancel"
              :disabled="deletingDeal"
              @click="cancelDeleteDeal"
            >Cancelar</button>
            <button
              class="del-btn del-btn-confirm"
              :disabled="deletingDeal"
              @click="confirmDeleteDeal"
            >{{ deletingDeal ? 'Eliminando…' : '🗑 Eliminar lead' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ━━━━━━━━━━ T2 · Toolbar dual rows (Kommo-style M1.B) ━━━━━━━━━━ */
.t2-toolbar {
  position: relative;
  margin: 0;
  background: linear-gradient(180deg, rgba(20,20,20,0.92), rgba(8,8,8,0.92));
  border: 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  border-radius: 0;
  box-shadow: 0 6px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04);
  overflow: visible;
  flex-shrink: 0;
}
/* Facetas diagonales decorativas eliminadas (Victor 2026-05-20): la
   diagonal cruzaba por detrás del buscador y se veía como una "caja
   fantasma". Solo queda el radial dorado tenue en la esquina. */
.t2-toolbar::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at 100% 0%, rgba(251,191,36,0.05), transparent 55%);
}

/* === ROW 1 · identidad + stats + CTA === */
.t2-row-top {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  position: relative;
  z-index: 1;
}
.t2-pipeline-large-wrap { position: relative; flex-shrink: 0; }
.t2-pipeline-large {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
  font-family: inherit;
  color: #fff;
}
.t2-pipeline-large.is-open .t2-pipeline-icon-lg {
  box-shadow: 0 4px 14px rgba(251,191,36,0.55), inset 0 1px 0 rgba(255,255,255,0.4);
}
.t2-pipeline-icon-lg {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
  color: #1a1209;
  font-weight: 900;
  box-shadow: 0 3px 10px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.3);
  flex-shrink: 0;
  transition: all 0.18s;
}
.t2-pipeline-info { line-height: 1.2; text-align: left; min-width: 0; }
.t2-pipeline-name {
  font-size: 15px; font-weight: 900; color: #fff;
  display: flex; align-items: center; gap: 6px;
  white-space: nowrap;
}
.t2-pipeline-name .chev {
  font-size: 10px;
  color: rgba(255,255,255,0.5);
  transition: transform 0.2s;
}
.t2-pipeline-tagline {
  font-size: 10.5px;
  color: rgba(255,255,255,0.5);
  margin-top: 2px;
  letter-spacing: 0.3px;
}

.t2-stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: auto;
}
.t2-stat-card {
  display: flex;
  flex-direction: column;
  padding: 4px 16px;
  border-right: 1px solid rgba(255,255,255,0.08);
}
.t2-stat-card:last-of-type { border-right: 0; padding-right: 16px; }
.t2-stat-label {
  font-size: 9.5px;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
}
.t2-stat-value {
  font-size: 17px;
  font-weight: 900;
  color: #fff;
  margin-top: 2px;
  line-height: 1;
}
.t2-stat-value.gold { color: #fbbf24; }
.kb-cards-loading {
  display: inline-block;
  color: #c9a35a;
  animation: kb-spin 0.9s linear infinite;
}
@keyframes kb-spin { to { transform: rotate(360deg); } }

.t2-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  height: 38px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border: 0;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.4px;
  box-shadow: 0 3px 14px rgba(251,191,36,0.35);
  flex-shrink: 0;
  font-family: inherit;
  transition: all 0.15s;
}
.t2-cta:hover { box-shadow: 0 5px 20px rgba(251,191,36,0.50); transform: translateY(-1px); }
.t2-cta .plus { font-size: 16px; line-height: 1; }

/* === ROW 2 · vista + chips + búsqueda + gear === */
.t2-row-bottom {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  position: relative;
  z-index: 1;
}

.t2-view-toggle {
  display: flex;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 2px;
  flex-shrink: 0;
}
.t2-view-btn {
  width: 30px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px;
  color: rgba(255,255,255,0.45);
  border: 0; background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
}
.t2-view-btn.active { background: rgba(255,255,255,0.10); color: #fff; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.08); }
.t2-view-btn.is-disabled { opacity: 0.35; cursor: not-allowed; }

.t2-chips {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.t2-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 999px;
  font-size: 11px;
  color: rgba(255,255,255,0.65);
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: all 0.15s;
}
.t2-chip:hover:not(.active) { color: #fff; background: rgba(255,255,255,0.07); }
.t2-chip.active {
  background: rgba(74,222,128,0.12);
  border-color: rgba(74,222,128,0.30);
  color: #4ade80;
  font-weight: 700;
}
.t2-chip.active::before { content: '●'; font-size: 6px; }
/* Chip "En atraso" (Victor 2026-06-08): rojo 911, sin bullet (ya tiene 🚨).
   Rojo también inactivo (Victor 2026-06-11): reemplaza "Sin asignar" y debe
   resaltar en rojo siempre, no solo cuando está activo. */
.t2-chip-atraso { color: #f87171; border-color: rgba(239,68,68,0.22); }
.t2-chip-atraso:hover:not(.active) { color: #fca5a5; background: rgba(239,68,68,0.08); }
.t2-chip-atraso.active {
  background: rgba(239,68,68,0.14);
  border-color: rgba(239,68,68,0.40);
  color: #f87171;
}
.t2-chip-atraso.active::before { content: none; }
.t2-chip-atraso.active:hover { background: rgba(239,68,68,0.22); }

/* Chips de sucursal (Victor 2026-06-09): dorado en vez de verde para diferenciar
   del filtro de estado. Activo = sucursal seleccionada. */
.t2-branch-divider {
  width: 1px;
  height: 20px;
  background: rgba(255,255,255,0.12);
  flex-shrink: 0;
  margin: 0 2px;
}
.t2-chip-branch:hover:not(.active) {
  color: #e7cf9a;
  background: rgba(201,163,90,0.10);
  border-color: rgba(201,163,90,0.30);
}
.t2-chip-branch.active {
  background: rgba(201,163,90,0.16);
  border-color: rgba(201,163,90,0.55);
  color: #e7cf9a;
  font-weight: 700;
  box-shadow: 0 0 0 1px rgba(201,163,90,0.22), 0 4px 14px rgba(201,163,90,0.12);
}
/* El punto de color (.t2-branch-dot) reemplaza el bullet dorado genérico. */
.t2-branch-dot {
  width: 7px; height: 7px; border-radius: 50%;
  flex-shrink: 0; display: inline-block;
}

/* Control segmentado de estados (Propuesta 1 · Victor 2026-06-11) ─────────── */
.t2-seg {
  display: inline-flex;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 2px;
  flex-shrink: 0;
}
.t2-seg-btn {
  display: inline-flex; align-items: center; gap: 5px;
  border: none; background: transparent;
  color: rgba(255,255,255,0.62);
  font-family: inherit; font-size: 11.5px;
  padding: 5px 11px; border-radius: 8px;
  cursor: pointer; white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.t2-seg-btn:hover:not(.on) { color: #fff; }
.t2-seg-btn.on { background: rgba(255,255,255,0.08); color: #fff; }
.t2-seg-btn.t2-seg-abiertos.on { background: rgba(74,222,128,0.13); color: #4ade80; }
.t2-seg-btn.t2-seg-atraso { color: #f87171; }
.t2-seg-btn.t2-seg-atraso.on { background: rgba(239,68,68,0.15); color: #f87171; }

/* Empuja el buscador a la derecha y lo deja compacto (menos cargado). */
.t2-spacer { flex: 1; min-width: 8px; }

/* Buscador unificado (Victor 2026-05-20 · propuesta A): UNA sola caja
   limpia. Fondo sólido para que el degradado diagonal del toolbar no se
   transparente por detrás (eso causaba el efecto "caja de fondo"). */
.t2-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  height: 38px;
  background: rgba(22,22,25,0.92);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px;
  transition: border-color 0.18s, background 0.18s;
  min-width: 220px;
}
/* Buscador compacto (Propuesta 1 · Victor 2026-06-11): ancho fijo a la derecha
   en vez de ocupar todo el espacio (se veía demasiado cargado). */
.t2-search--compact {
  flex: 0 0 auto;
  width: 280px;
  height: 34px;
}
.t2-search:hover {
  border-color: rgba(251,191,36,0.28);
}
.t2-search:focus-within {
  border-color: rgba(251,191,36,0.45);
  background: rgba(28,28,32,0.95);
}
.t2-search-icon { color: rgba(255,255,255,0.45); font-size: 12px; }
.t2-search input {
  flex: 1;
  background: transparent;
  border: 0;
  outline: 0;
  color: #fff;
  font-size: 12.5px;
  font-family: inherit;
}
.t2-search input::placeholder { color: rgba(255,255,255,0.40); }
.t2-search-ai-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.10));
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  color: #fbbf24;
  letter-spacing: 0.4px;
}
.t2-search-kbd kbd {
  display: inline-block;
  padding: 2px 6px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 4px;
  font-family: ui-monospace, monospace;
  font-size: 9px;
  color: rgba(255,255,255,0.65);
}

/* Resultados del buscador inteligente (cross-embudo) */
.ksr-panel {
  position: absolute; left: 0; right: 0; top: calc(100% + 8px); z-index: 60;
  max-height: 420px; overflow-y: auto;
  background: rgba(10,10,10,0.97);
  border: 1.5px solid rgba(255,255,255,0.12);
  border-radius: 14px;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 22px 50px rgba(0,0,0,0.65);
  padding: 6px;
}
.ksr-empty { padding: 16px; text-align: center; font-size: 12px; color: rgba(255,255,255,0.45); }
.ksr-row {
  display: flex; align-items: center; gap: 11px; width: 100%; text-align: left;
  padding: 10px 12px; border-radius: 10px; background: transparent;
  border: none; cursor: pointer; transition: background 0.14s;
}
.ksr-row:hover { background: rgba(251,191,36,0.07); }
.ksr-av {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800;
  background: linear-gradient(135deg,#475569,#334155); color: #cbd5e1;
}
.ksr-info { flex: 1; min-width: 0; }
.ksr-name { display: block; font-size: 13px; font-weight: 800; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ksr-sub { display: block; font-size: 10px; color: rgba(255,255,255,0.5); margin-top: 2px;
  font-family: ui-monospace, monospace; }
.ksr-head { padding: 7px 12px 8px; font-size: 9.5px; letter-spacing: 0.5px;
  text-transform: uppercase; color: rgba(255,255,255,0.4); font-weight: 700; }
.ksr-rank { width: 18px; flex-shrink: 0; text-align: center; font-size: 12px;
  font-weight: 800; color: #fbbf24; align-self: center; }
.ksr-tags { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
/* Chip de sucursal en resultados de búsqueda (Victor 2026-06-09 · Propuesta 1):
   color propio por sucursal para identificarla de un vistazo. */
.ksr-branch-chip {
  font-size: 9px; font-weight: 800; letter-spacing: 0.3px;
  padding: 2px 7px; border-radius: 999px; white-space: nowrap;
  color: var(--brc); background: var(--brc-bg); border: 1px solid var(--brc-bd);
}
.ksr-branch-chip.br-sdo { --brc:#7fb3e0; --brc-bg:rgba(91,155,213,0.16); --brc-bd:rgba(91,155,213,0.45); }
.ksr-branch-chip.br-sde { --brc:#7fd49b; --brc-bg:rgba(94,194,126,0.16); --brc-bd:rgba(94,194,126,0.45); }
.ksr-branch-chip.br-stg { --brc:#bfa3ea; --brc-bg:rgba(169,139,219,0.16); --brc-bd:rgba(169,139,219,0.45); }
.ksr-branch-chip.br-lr  { --brc:#eaa877; --brc-bg:rgba(224,146,90,0.16);  --brc-bd:rgba(224,146,90,0.45); }
.ksr-pipe {
  font-size: 9px; font-weight: 800; letter-spacing: 0.4px; color: #fbbf24;
  background: rgba(251,191,36,0.12); border: 1px solid rgba(251,191,36,0.30);
  padding: 2px 7px; border-radius: 6px; white-space: nowrap;
}
.ksr-stage-chip { font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 6px;
  border: 1px solid; white-space: nowrap; }
.ksr-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
.ksr-amt { font-size: 12px; font-weight: 800; color: #e7cf9a; white-space: nowrap; }
.ksr-assignee { font-size: 9px; color: rgba(255,255,255,0.4); white-space: nowrap; }
.ksr-go { font-size: 9px; font-weight: 800; color: #241c08;
  background: linear-gradient(180deg,#e7cf9a,#c9a35a); padding: 3px 8px; border-radius: 7px; white-space: nowrap; }

.t2-gear-wrap { position: relative; flex-shrink: 0; }
.t2-gear {
  width: 38px; height: 38px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(251,191,36,0.18);
  border: 1px solid rgba(251,191,36,0.40);
  border-radius: 9px;
  color: #fbbf24;
  cursor: pointer;
  font-size: 16px;
  font-family: inherit;
  box-shadow: 0 0 16px rgba(251,191,36,0.30);
  transition: all 0.15s;
}
.t2-gear:hover, .t2-gear.active {
  background: rgba(251,191,36,0.28);
  color: #fde68a;
  border-color: rgba(251,191,36,0.60);
  box-shadow: 0 0 22px rgba(251,191,36,0.55);
}

.t2-gear-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 260px;
  z-index: 60;
  background: #0a0a0a;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  border: 1px solid rgba(251,191,36,0.40);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(251,191,36,0.10), 0 10px 30px rgba(0,0,0,0.60), 0 0 0 1px rgba(255,255,255,0.03) inset;
  overflow: hidden;
}

.t2-gear-menu-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #fbbf24;
  border-bottom: 1px solid rgba(251,191,36,0.18);
  background: linear-gradient(180deg, rgba(251,191,36,0.06), transparent);
  position: relative;
  z-index: 1;
}
.t2-gear-menu-header::before {
  content: '⚙';
  font-size: 13px;
}
.t2-gear-menu-inner {
  position: relative;
  padding: 4px;
}
.t2-gear-menu-inner::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.08) 50%, transparent 52%),
    radial-gradient(circle at 100% 0%, rgba(255,255,255,0.12), transparent 65%);
}
.t2-gear-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: rgba(255,255,255,0.85);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  position: relative;
  z-index: 1;
}
.t2-gear-item:hover { background: rgba(255,255,255,0.06); color: #fff; }
.t2-gear-item .ico {
  width: 22px;
  text-align: center;
  font-size: 13px;
  color: rgba(255,255,255,0.55);
}
.t2-gear-item.gold { color: #fbbf24; }
.t2-gear-item.gold .ico { color: #fbbf24; }
.t2-gear-item.gold:hover { background: rgba(251,191,36,0.10); }
.t2-gear-item.danger { color: #f87171; }
.t2-gear-item.danger .ico { color: #f87171; }
.t2-gear-item.danger:hover { background: rgba(239,68,68,0.10); }
.t2-gear-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 4px 8px;
  position: relative;
  z-index: 1;
}

/* ━━━━━━━━━━ L4 · Lead Card (Channel Border) ━━━━━━━━━━ */
/* Columna INVISIBLE (Victor 2026-05-20 · I4): sin caja al reposo (fondo
   negro puro del board); al pasar el mouse, un realce apenas perceptible. */
/* Columna sin resalte al hover (Victor 2026-05-21): nada de rectángulo
   blanco. La señal de "dónde estás" la da la tarjeta, no la columna. */
.kb-col {
  background: transparent;
}

/* Tablero: cursor "manita" para arrastrar y panear desde el fondo. */
.kb-board { cursor: grab; }
.kb-board.kb-grabbing { cursor: grabbing; user-select: none; }

/* Tarjeta I4 · flotante premium: gris, sombra fuerte, más padding. */
.lc-card {
  position: relative;
  padding: 13px 14px;
  background: #1c1c20;
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 14px;
  cursor: grab;
  display: flex;
  gap: 10px;
  box-shadow: 0 10px 26px rgba(0,0,0,0.6);
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.2s ease, border-color 0.2s ease;
}
/* Hover B (Victor 2026-05-21): la tarjeta bajo el cursor sube + crece y
   flota por encima de las demás. */
.lc-card:hover {
  transform: translateY(-5px) scale(1.025);
  border-color: rgba(255,255,255,0.18);
  box-shadow: 0 22px 44px rgba(0,0,0,0.8);
  z-index: 5;
}
.lc-card:active { cursor: grabbing; }
/* Borde lateral de color por canal eliminado (F4): el canal se indica solo
   con el icono pequeño en la fila del nombre. */

.lc-avatar, .lc-avatar-img {
  width: 32px; height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-weight: 800;
  font-size: 11px;
  color: #fff;
}
.lc-avatar-img { object-fit: cover; }
.lc-avatar-neutral {
  background: rgba(148,163,184,0.20);
  color: rgba(148,163,184,0.85);
  border: 1.5px dashed rgba(148,163,184,0.40);
}
/* Wrap del avatar · permite anclar el badge del canal en la esquina sin
   romper el layout flex de la card (Victor 2026-05-31). */
.lc-avatar-wrap {
  position: relative;
  width: 32px; height: 32px;
  flex-shrink: 0;
}
.lc-ch-badge {
  position: absolute;
  bottom: -2px; right: -2px;
  width: 16px; height: 16px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #1c1c20;
  color: #fff;
  pointer-events: none;
}
.lc-ch-badge-wa { background: #25d366; }
.lc-ch-badge-ig { background: linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%); }
.lc-ch-badge-fb { background: #1877f2; }
.lc-ch-badge-tk { background: #000; border-color: rgba(105,201,208,.6); }
.lc-ch-badge-email,
.lc-ch-badge-tw,
.lc-ch-badge-web { display: none; }

.lc-info { flex: 1; min-width: 0; }

.lc-row1 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding-right: 42px; /* espacio para el badge de días en la esquina */
}
.lc-name {
  font-size: 12.5px;
  font-weight: 800;
  color: #fff;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lc-ch-icon {
  width: 18px; height: 18px;
  border-radius: 5px;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px;
  flex-shrink: 0;
  color: #fff;
  font-weight: 900;
}
.lc-ch-icon-wa { background: #25d366; }
.lc-ch-icon-ig { background: linear-gradient(135deg, #fcb045, #fd1d1d, #833ab4); }
.lc-ch-icon-fb { background: #1877F2; }
.lc-ch-icon-tw { background: #1da1f2; }
.lc-ch-icon-email { background: #94a3b8; }
.lc-ch-icon-web { background: rgba(255,255,255,0.20); color: rgba(255,255,255,0.75); }

.lc-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 6px;
}
.lc-meta-row {
  font-size: 10.5px;
  color: rgba(255,255,255,0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lc-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.05);
  font-size: 9.5px;
  color: rgba(255,255,255,0.50);
}
/* Badge días en la etapa · semáforo (P2 · Victor 2026-05-24).
   Esquina superior derecha · color = antigüedad en la columna actual. */
.lc-days-badge {
  position: absolute;
  top: 10px;
  right: 11px;
  z-index: 2;
  font-size: 9.5px;
  font-weight: 900;
  line-height: 1.3;
  padding: 2px 7px;
  border-radius: 999px;
  white-space: nowrap;
  backdrop-filter: blur(4px);
}
.lc-days-fresh { background: rgba(31,157,107,0.18); color: #4ade80; border: 1px solid rgba(74,222,128,0.30); }
.lc-days-warn  { background: rgba(201,163,90,0.18); color: #c9a35a; border: 1px solid rgba(201,163,90,0.30); }
.lc-days-stale { background: rgba(220,80,80,0.16); color: #f87171; border: 1px solid rgba(248,113,113,0.30); }

/* Latido (P4 · Victor 2026-05-24) · SOLO DTO. DE RETOQUE, +10 días en etapa.
   Se detiene al pasar el cursor para no estorbar el arrastre. */
.lc-beat {
  animation: lcBeat 1.6s ease-in-out infinite;
  transform-origin: center;
}
@keyframes lcBeat {
  0%, 100% { transform: scale(1);     box-shadow: 0 10px 26px rgba(0,0,0,0.6); }
  15%      { transform: scale(1.035); box-shadow: 0 14px 30px rgba(0,0,0,0.7), 0 0 16px rgba(248,113,113,0.45); }
  30%      { transform: scale(1); }
  45%      { transform: scale(1.02); }
  60%      { transform: scale(1); }
}
.lc-beat:hover { animation: none; }

/* "El cliente escribió" SIN LEER (variante B · Victor 2026-05-25): burbuja azul
   compacta con pocas palabras + "…" y globo "💬 N" al lado con la cantidad de
   mensajes sin leer. Mismo lenguaje visual que la lista de chats. */
.lc-unread {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 7px;
}
.lc-unread-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 160px;
  padding: 3px 9px;
  background: rgba(74,158,255,0.12);
  border: 1px solid rgba(74,158,255,0.30);
  border-radius: 999px;
}
.lc-unread-ico { font-size: 11px; flex-shrink: 0; line-height: 1; }
.lc-unread-tx {
  font-size: 10.5px;
  font-weight: 700;
  color: #9ccbff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lc-unread-cnt {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 999px;
  background: #2f80ed;
  color: #fff;
  font-size: 9px;
  font-weight: 900;
  box-shadow: 0 0 8px rgba(74,158,255,0.6);
}

/* ━━━━━━━━━━ Menú ⋮ Eliminar lead (Victor 2026-05-30) ━━━━━━━━━━
   Solo SuperAdmin + Victor Manuel ven el botón. A la izquierda del badge
   de días (top:10/right:11) para no chocar. */
.lc-kebab-wrap {
  position: absolute;
  top: 6px;
  right: 38px;
  z-index: 6;
}
.lc-kebab {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  color: rgba(255,255,255,0.35);
  font-size: 16px;
  font-weight: 900;
  line-height: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s ease;
}
.lc-card:hover .lc-kebab {
  color: rgba(255,255,255,0.65);
  border-color: rgba(255,255,255,0.10);
}
.lc-kebab:hover,
.lc-kebab.lc-kebab-open {
  color: #c9a35a;
  background: rgba(201,163,90,0.16);
  border-color: rgba(201,163,90,0.40);
}
.lc-menu {
  position: absolute;
  top: 28px;
  right: 0;
  width: 248px;
  background: linear-gradient(180deg, #1b1b21, #15151a);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 11px;
  overflow: hidden;
  box-shadow: 0 24px 50px rgba(0,0,0,0.7),
              0 0 0 1px rgba(201,163,90,0.10) inset;
  z-index: 100;
}
.lc-menu-item {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 11px 14px;
  font-size: 12.5px;
  color: #fff;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.lc-menu-item:hover { background: rgba(255,255,255,0.04); }
.lc-menu-ic {
  width: 18px;
  text-align: center;
  font-size: 13px;
  flex-shrink: 0;
  padding-top: 1px;
}
.lc-menu-tx { display: flex; flex-direction: column; gap: 2px; }
.lc-menu-tx-main { font-size: 12.5px; font-weight: 700; line-height: 1.2; }
.lc-menu-tx-sub { font-size: 10.5px; color: rgba(255,255,255,0.45); font-weight: 500; line-height: 1.35; }

.lc-menu-item-danger { color: #fca5a5; }
.lc-menu-item-danger:hover { background: rgba(239,68,68,0.14); }
.lc-menu-item-danger .lc-menu-ic { color: #ef4444; }

/* ━━━━━━━━━━ Modal confirmar eliminar lead (Victor 2026-05-30) ━━━━━━━━━━ */
.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.del-modal {
  background: linear-gradient(180deg, #1b1b21, #15151a);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 18px;
  overflow: hidden;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 40px 80px rgba(0,0,0,0.75),
              0 0 0 1px rgba(201,163,90,0.08) inset;
}
.del-top { height: 3px; background: linear-gradient(90deg, #ef4444, rgba(239,68,68,0.05)); }
.del-body { padding: 28px 26px 22px; text-align: center; }
.del-ico {
  width: 60px; height: 60px;
  border-radius: 16px;
  background: rgba(239,68,68,0.15);
  border: 1px solid rgba(239,68,68,0.40);
  display: flex; align-items: center; justify-content: center;
  font-size: 28px;
  margin: 0 auto 16px;
}
.del-ttl { font-size: 17px; font-weight: 800; color: #fff; margin-bottom: 8px; letter-spacing: -0.2px; }
.del-desc { font-size: 13px; color: rgba(255,255,255,0.65); line-height: 1.55; margin-bottom: 20px; }
.del-desc b { color: #fff; }
.del-leadbox {
  background: rgba(0,0,0,0.30);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 11px;
  padding: 12px 14px;
  margin-bottom: 18px;
  text-align: left;
}
.del-leadbox-name { font-size: 13px; font-weight: 800; color: #fff; }
.del-leadbox-sub { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 3px; }
.del-keep {
  background: linear-gradient(180deg, rgba(34,197,94,0.06), rgba(34,197,94,0.02));
  border: 1px solid rgba(34,197,94,0.30);
  border-radius: 11px;
  padding: 11px 14px;
  margin-bottom: 18px;
  text-align: left;
}
.del-keep-lbl {
  font-size: 9.5px; font-weight: 800; letter-spacing: 1.5px;
  color: #86efac; text-transform: uppercase; margin-bottom: 6px;
}
.del-keep-li {
  font-size: 11.5px;
  color: rgba(255,255,255,0.65);
  line-height: 1.5;
  padding-left: 14px;
  position: relative;
  margin-top: 4px;
}
.del-keep-li::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #86efac;
  font-weight: 800;
}
.del-actions { display: flex; gap: 10px; }
.del-btn {
  flex: 1;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid transparent;
  letter-spacing: 0.1px;
  transition: all 0.15s ease;
}
.del-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.del-btn-cancel {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.07);
  color: #fff;
}
.del-btn-cancel:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
.del-btn-confirm {
  background: linear-gradient(180deg, #ef4444, #dc2626);
  color: #fff;
  border-color: #dc2626;
  box-shadow: 0 6px 18px -4px rgba(239,68,68,0.50);
}
.del-btn-confirm:hover:not(:disabled) {
  background: linear-gradient(180deg, #f87171, #ef4444);
}

</style>
